import React, {useMemo} from "react";
import PropTypes from "prop-types";
import {t} from "i18next";
import CardList from "components/Card/CardList/CardList";
import DrawerSelectAll from "components/Drawer/components/DrawerSelectAll/DrawerSelectAll";
import {EntityListProvider} from "contexts/EntityListContext";
import {ENTITY_TYPE} from "utils/entities";
import PlusMore from "./Components/PlusMore";
import AssetListItem from "./EntityListItems/AssetListItem";
import IndicatorsListItem from "./EntityListItems/IndicatorsListItem";
import ObservablesListItem from "./EntityListItems/ObservablesListItem";
import RelationsListItem from "./EntityListItems/RelationsListItem";

/**
 * <EntityList> is designed as a re-usable list component to provide standard
 * styling & layout to EntityLists across the app.
 *
 * IMPORTANT: This component is designed to be configurable via props.
 *            Please run Storybook for examples of various configurations
 *            of this component.
 *
 * EntityList is the root component which should be imported by your component.
 * Please do not import or over-ride the child components for an individual
 * feature, use the exposed props to customize the view you want from this
 * component.
 *
 * If modifying props of this component or any child component, it should be
 * a UX update approved site-wide.
 *
 * Entity Types Supported:
 * - Assets
 * - Observables
 * - Indicators
 * - Relationships
 */
const EntityList = ({
  data = [],
  entityType,
  limit,
  showEventsCount = true,
  showPlusMore = true,
  plusMoreHandler,
  plusMore,
  showPivotMenu = true,
  showLinks = false,
  selectable = false,
  selected = [],
  setSelected,
  selectName,
  minimal = false,
  withDrawer = false
}) => {
  const label = t(`entity.label.${entityType}`);

  const isOverLimit = useMemo(
    () => Boolean(data?.length && data?.length > limit && limit > 0),
    [data, limit]
  );
  const remainingItems = useMemo(
    () => (isOverLimit ? data?.length - limit : 0),
    [isOverLimit, data, limit]
  );
  const visibleItems = useMemo(
    () => (isOverLimit ? data?.slice(0, limit) : data),
    [isOverLimit, data, limit]
  );

  /**
   * Build list of Item components based on `entityType`.
   */
  const entityListItems = visibleItems.map((entity, idx) => {
    const {type, value, title} = entity;
    const key = `${type}:${value}:${idx}`;
    const tooltip = {
      title: label,
      subtitle: value || title
    };

    const sharedProps = {
      ...entity,
      entity,
      tooltip,
      showEventsCount,
      showPivotMenu,
      minimal
    };

    switch (entityType) {
      case ENTITY_TYPE.assets:
        return <AssetListItem key={key} {...{...sharedProps}} />;
      case ENTITY_TYPE.observables:
        return <ObservablesListItem key={key} {...{...sharedProps}} />;
      case ENTITY_TYPE.indicators:
        return (
          <IndicatorsListItem
            key={`${value || title}:${idx}`}
            value={value || title}
            showLinks={showLinks}
            {...{...sharedProps}}
          />
        );
      case ENTITY_TYPE.relations:
        return <RelationsListItem key={key} {...{...sharedProps}} />;
      default:
        return null;
    }
  });

  return entityListItems ? (
    <EntityListProvider
      {...{
        data,
        entityType,
        selectable,
        selected,
        setSelected,
        withDrawer
      }}>
      {selectable && <DrawerSelectAll {...{selectName}} />}
      <div data-testid={"EntityList"}>
        <CardList>{entityListItems}</CardList>
        {isOverLimit && showPlusMore && (
          <PlusMore
            customRenderer={plusMore}
            remaining={remainingItems}
            handleClick={plusMoreHandler}
          />
        )}
      </div>
    </EntityListProvider>
  ) : null;
};

EntityList.propTypes = {
  /**
   * Array of Entities.
   */
  data: PropTypes.array,
  /**
   * Defines the Entity Type to tailor how the data is presented.
   */
  entityType: PropTypes.oneOf(Object.values(ENTITY_TYPE)),
  /**
   * Only render the first n items of the array, where limit={n}.
   * By default, and if `limit` is null or undefined,
   * all items in the array with display.
   */
  limit: PropTypes.number,
  /**
   * Default: true
   *
   * If the dataset contains an event count, it will render on the
   * right side by default.
   * If no count is provided by the dataset, nothing will render.
   * This has no effect where minimal=true, minimal list items never
   * show the count value.
   *
   * showEventsCount={false} to disable display of the event count.
   */
  showEventsCount: PropTypes.bool,
  /**
   * Default: true.
   *
   * by default a `<PlusMoreTag/>` will be shown below the list if:
   * - `limit` props is defined.
   * - data.length > limit
   *
   * To disable this behavior set showEventsCount={false}
   */
  showPlusMore: PropTypes.bool,
  /**
   * A callback which will execute when the user clicks the "PlusMore" element.
   */
  plusMoreHandler: PropTypes.func,
  /**
   * Override the default `PlusMoreTag` by passing another React component
   * to this prop.
   */
  plusMore: PropTypes.func,
  /**
   * Default: true.
   *
   * Set showPivotMenu={false} to remove PivotMenu's.
   */
  showPivotMenu: PropTypes.bool,
  /**
   * Default: false.
   * Currently only applies to Indicators.
   *
   * With showLinks=true, the title component will become a link when an
   * Indicator includes a valid source_uri value.
   */
  showLinks: PropTypes.bool,
  /**
   * Default: false.
   * If true, a checkbox will render before each list item.
   */
  selectable: PropTypes.bool,
  /**
   * State for selected checkboxes
   * Requires selectable={true} and a function to be assigned to setSelected
   */
  selected: PropTypes.array,
  /**
   * Function to set selected checkbox state
   * Requires selectable={true} and selected to have a value set to it
   */
  setSelected: PropTypes.func,
  /**
   * Adds a name to be shown in the select header for what's being selected
   * (e.g. "IP")
   */
  selectName: PropTypes.string,
  /**
   * Default Value: false - Render normal view (Cards, Drawers, etc.)
   * true - Renders a more compact view used by Tables. note that some sections
   * like 'subtitle' and 'rightSide' event counts are never show in this mode.
   */
  minimal: PropTypes.bool,
  /**
   * If true, drawer specific styling or tooltip position will be applied
   *
   * @defaultValue false
   */
  withDrawer: PropTypes.bool
};

export default EntityList;
